import {computed, ref} from 'vue'
import {defineStore} from 'pinia'

export type MeetingView = 'lobby' | 'call' | 'draw' | 'screensharing'
export type DrawMode = 'default' | 'select'

// Same transitions as the former `app.view.meeting` machine state:
// lobby -> call <-> draw | screensharing, back to lobby from call, draw and screensharing.
// Draw has a sub state: default <-> select
export default defineStore('meeting', () => {
  const view = ref<MeetingView>('lobby')
  const drawMode = ref<DrawMode>('default')

  const isLobby = computed(() => view.value === 'lobby')
  const isCalling = computed(() => view.value === 'call')
  const isDraw = computed(() => view.value === 'draw')
  const isScreensharing = computed(() => view.value === 'screensharing')
  const isDrawDefault = computed(() => isDraw.value && drawMode.value === 'default')
  const isDrawSelect = computed(() => isDraw.value && drawMode.value === 'select')
  // 'lobby' | 'call' | 'screensharing' | 'draw.default' | 'draw.select'
  const path = computed(() => (view.value === 'draw' ? `draw.${drawMode.value}` : view.value))

  function startCall() {
    if (view.value === 'lobby') view.value = 'call'
  }

  function startDraw() {
    if (view.value === 'call') {
      drawMode.value = 'default'
      view.value = 'draw'
    }
  }

  function leaveDraw() {
    if (view.value === 'draw') view.value = 'call'
  }

  function startScreensharing() {
    if (view.value === 'call') view.value = 'screensharing'
  }

  function stopScreensharing() {
    if (view.value === 'screensharing') view.value = 'call'
  }

  function toLobby() {
    if (view.value !== 'lobby') view.value = 'lobby'
  }

  function selectDraw() {
    if (isDraw.value && drawMode.value === 'default') drawMode.value = 'select'
  }

  function resetDraw() {
    if (isDraw.value && drawMode.value === 'select') drawMode.value = 'default'
  }

  return {
    view,
    drawMode,
    isLobby,
    isCalling,
    isDraw,
    isScreensharing,
    isDrawDefault,
    isDrawSelect,
    path,
    startCall,
    startDraw,
    leaveDraw,
    startScreensharing,
    stopScreensharing,
    toLobby,
    selectDraw,
    resetDraw,
  }
})
